import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  MMEmbeddableZone,
  useEmbeddedEffect,
} from '@machinemetrics/mm-react-embeddable';
import i18n from 'i18next';

import { LABOR_TICKET_HOST } from 'lib/appConfig';
import {
  actionCloseSetupActionModal,
  actionSetEmbeddedLaborTicket,
} from 'lib/actions';
import { getSetupActionModalOpen } from 'lib/selectors/getSetupActionModalOpen';
import { getCurrentLaborTicket } from 'lib/selectors/getLaborTickets';
import { getEmbeddedLaborTicket } from 'lib/selectors/getEmbeddedLaborTicket';
import { getLaborTicketAction } from 'lib/selectors/getLaborTicketAction';
import { getEnableOperatorVerification } from 'lib/selectors/getEnableOperatorVerification';
import { useRequestRecentActivitySets } from 'lib/hooks';
import useHandleLaborTicketSubmit from './hooks/useHandleLaborTicketSubmit';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1400;
`;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: transparent;
  overflow: hidden;
`;

const EmbeddedContainer = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
  position: relative;

  /* Ensure the iframe fills the container */
  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const EmbeddedSetupAction = ({ id, path, personId }) => {
  const dispatch = useDispatch();
  const { refetch: refetchRecentActivitySets } = useRequestRecentActivitySets();
  const laborTicketAction = useSelector(getLaborTicketAction);
  const enableOperatorVerification = useSelector(getEnableOperatorVerification);
  const isOpen = useSelector(getSetupActionModalOpen);

  // Create close modal callback for the submit handler
  const closeModal = useCallback(() => {
    dispatch(actionCloseSetupActionModal());
    dispatch(actionSetEmbeddedLaborTicket(null));
  }, [dispatch]);

  const handleSubmit = useHandleLaborTicketSubmit(closeModal);

  // Handle close event
  useEmbeddedEffect(id, 'setup-action:close', () => {
    dispatch(actionCloseSetupActionModal());
    refetchRecentActivitySets();
  });

  // Handle submit event
  useEmbeddedEffect(id, 'setup-action:submit', handleSubmit);

  return (
    <MMEmbeddableZone
      id={id}
      src={`${LABOR_TICKET_HOST}`}
      path={path}
      context={{
        personId,
        action: laborTicketAction,
        enableOperatorVerification,
        opened: isOpen,
      }}
      params={{
        language: i18n.language,
      }}
    />
  );
};

export const SetupActionModal = () => {
  const id = 'setup-action-form';
  const isOpen = useSelector(getSetupActionModalOpen);
  const embeddedLaborTicket = useSelector(getEmbeddedLaborTicket);
  const currentLaborTicket = useSelector(getCurrentLaborTicket);

  const liveLaborTicket = embeddedLaborTicket || currentLaborTicket;

  // Pin the labor ticket when modal opens to prevent race condition
  const [pinnedLaborTicket, setPinnedLaborTicket] = useState(null);

  useEffect(() => {
    if (isOpen && liveLaborTicket && !pinnedLaborTicket) {
      setPinnedLaborTicket(liveLaborTicket);
    } else if (!isOpen && pinnedLaborTicket) {
      setPinnedLaborTicket(null);
    }
  }, [isOpen, liveLaborTicket, pinnedLaborTicket]);

  const laborTicket = pinnedLaborTicket || liveLaborTicket;
  const laborTicketRef = laborTicket?.laborTicketRef;
  const path = laborTicketRef ? `/setup-action/${laborTicketRef}` : null;

  if (!isOpen || !laborTicket || !laborTicketRef) {
    return null;
  }

  return (
    <ModalOverlay>
      <ModalContainer>
        <EmbeddedContainer>
          <EmbeddedSetupAction
            id={id}
            path={path}
            personId={laborTicket.personId}
          />
        </EmbeddedContainer>
      </ModalContainer>
    </ModalOverlay>
  );
};
